import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { films, getFilmById } from "@/mocks/films";
import FilmCard from "@/components/feature/FilmCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShareModal from "@/components/feature/ShareModal";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const film = id ? getFilmById(id) : undefined;
  const { isFavorite, toggle } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);

  useSeo({
    title: film
      ? `《${film.title}》剧情介绍与演员详情 - 顽皮的女朋友`
      : "影视详情 - 顽皮的女朋友",
    description: film
      ? `${film.title}（${film.year}）${film.type}，由${film.director}执导，${film.cast.join(
          "、"
        )}主演。${film.synopsis}`
      : undefined,
    keywords: film
      ? `${film.title},剧情介绍,演员,${film.genres.join(",")}`
      : undefined,
  });

  const related = useMemo(() => {
    if (!film) return [];
    return films
      .filter(
        (item) =>
          item.id !== film.id &&
          (item.genres.some((genre) => film.genres.includes(genre)) ||
            item.cast.some((actor) => film.cast.includes(actor)))
      )
      .slice(0, 4);
  }, [film]);

  if (!film) {
    return (
      <div className="px-4 md:px-6 py-24 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="font-heading font-semibold text-xl text-foreground-950">
          未找到该影视作品
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          该影视内容可能已被移除，或链接有误。
        </p>
        <Link
          to="/category"
          className="mt-5 h-11 px-6 inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-heading text-sm tracking-wide hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回影视分类
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(film.id);

  return (
    <article>
      <div className="relative w-full h-[360px] md:h-[480px] overflow-hidden">
        <img
          src={film.backdrop}
          alt={`${film.title}剧照`}
          title={`${film.title}影视剧照`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/60 to-foreground-950/40"></div>
        <div className="absolute inset-0">
          <div className="px-4 md:px-6 pt-6">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "影视分类", to: "/category" },
                { label: film.title },
              ]}
            />
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 px-4 md:px-6 pb-6 md:pb-8">
          <div className="flex flex-col sm:flex-row gap-5 md:gap-7 items-start">
            <div className="hidden sm:block w-32 md:w-44 shrink-0 rounded-lg overflow-hidden border-2 border-background-50/30">
              <img
                src={film.poster}
                alt={`${film.title}海报`}
                title={`${film.title}影视作品海报`}
                className="w-full aspect-[2/3] object-cover object-top"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-bold text-3xl md:text-5xl text-background-50">
                {film.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs">
                  {film.type}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs backdrop-blur-sm">
                  {film.region}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs backdrop-blur-sm">
                  {film.year}
                </span>
                {film.genres.map((genre) => (
                  <span
                    key={genre}
                    className="px-2.5 py-1 rounded-md bg-accent-500/85 text-foreground-950 text-xs"
                  >
                    {genre}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-sm md:text-base text-background-100">
                主演：{film.cast.join("、")}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggle(film.id)}
                  className={
                    favorited
                      ? "h-11 px-5 inline-flex items-center gap-2 rounded-md bg-accent-500 text-foreground-950 font-heading text-sm tracking-wide hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                      : "h-11 px-5 inline-flex items-center gap-2 rounded-md border border-background-50/40 text-background-50 font-heading text-sm tracking-wide hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
                  }
                >
                  <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="h-11 px-5 inline-flex items-center gap-2 rounded-md border border-background-50/40 text-background-50 font-heading text-sm tracking-wide hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
        <div>
          <section>
            <h2 className="font-heading font-semibold text-2xl text-foreground-950 flex items-center gap-2">
              <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
              剧情简介
            </h2>
            <div className="mt-5 space-y-4">
              {film.plot.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-sm md:text-base leading-relaxed text-foreground-700"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="font-heading font-semibold text-2xl text-foreground-950 flex items-center gap-2">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              影视资料
            </h2>
            <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: "导演", value: film.director },
                { label: "类型", value: film.type },
                { label: "地区", value: film.region },
                { label: "上映年份", value: String(film.year) },
                { label: "题材", value: film.genres.join(" / ") },
                { label: "上映日期", value: film.releaseDate },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <span className="font-label text-sm text-foreground-500 w-16 shrink-0">
                    {row.label}
                  </span>
                  <span className="text-sm text-foreground-900">{row.value}</span>
                </div>
              ))}
            </dl>
            <div className="mt-4 p-4 rounded-lg bg-background-100 border border-background-200/70">
              <span className="font-label text-sm text-foreground-500">主演</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {film.cast.map((actor) => (
                  <span
                    key={actor}
                    className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-sm"
                  >
                    {actor}
                  </span>
                ))}
              </div>
            </div>
          </section>
        </div>

        <aside className="rounded-lg bg-primary-50 border border-primary-100 p-5">
          <h3 className="font-heading font-semibold text-base text-foreground-950 flex items-center gap-2">
            <i className="ri-fire-line text-accent-600"></i>
            热度指数
          </h3>
          <p className="mt-3 font-heading font-bold text-4xl text-primary-600">
            {film.heat.toLocaleString()}
          </p>
          <p className="mt-1 text-xs text-foreground-500">综合播放热度（演示数据）</p>
          <div className="mt-4 h-2 rounded-full bg-background-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-accent-500"
              style={{ width: `${Math.min(100, Math.round(film.heat / 100))}%` }}
            ></div>
          </div>
          <p className="mt-5 text-xs leading-relaxed text-foreground-600">
            数据为演示用途，不代表真实播放量。收藏状态保存在本地浏览器，仅供参考。
          </p>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="px-4 md:px-6 pb-14">
          <h2 className="font-heading font-semibold text-2xl text-foreground-950 flex items-center gap-2">
            <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
            相关推荐
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            根据题材与主演关联推荐
          </p>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {related.map((item) => (
              <FilmCard key={item.id} film={item} />
            ))}
          </div>
        </section>
      )}

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`《${film.title}》剧情介绍与演员详情`}
      />
    </article>
  );
}